import { Form, Formik, type FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FormField } from "@/components/common/form-field";
import { PhoneField, ZipField } from "@/components/common/masked-form-field";
import { getFirm, updateFirm } from "@/features/onboarding/api";
import { type FirmContactValues, firmContactSchema } from "@/features/onboarding/schema";
import type { FirmProfile } from "@/features/settings/firm-api";
import { applyApiErrors } from "@/lib/form-errors";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

const EMPTY: FirmContactValues = {
  email: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
};

/** Step 1 — firm contact + address. Loads the firm so the PATCH carries its
 * name + billing config through untouched (see the original FirmContactStep). */
export function CompanyDetailsStep({
  meta,
  index,
  isFirst,
  onBack,
  onSkip,
  onSaved,
}: OnboardingStepProps) {
  const [firm, setFirm] = useState<FirmProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    getFirm()
      .then((f) => active && setFirm(f))
      .catch(() => active && toast.error("Couldn't load your firm details."));
    return () => {
      active = false;
    };
  }, []);

  const save = async (values: FirmContactValues, { setErrors }: FormikHelpers<FirmContactValues>) => {
    // Can't build a safe payload until the firm (and its billing config) loads —
    // treat Save as advance rather than risk clobbering unseen fields.
    if (!firm) {
      onSaved();
      return;
    }
    setSubmitting(true);
    try {
      await updateFirm({
        ...firm,
        email: values.email.trim(),
        phone: values.phone.trim(),
        addressLine1: values.addressLine1.trim(),
        addressLine2: values.addressLine2.trim(),
        city: values.city.trim(),
        state: values.state.trim().toUpperCase(),
        zipCode: values.zipCode.trim(),
      });
      onSaved();
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save your firm details." }));
      setSubmitting(false);
    }
  };

  const initialValues: FirmContactValues = firm
    ? {
        email: firm.email,
        phone: firm.phone,
        addressLine1: firm.addressLine1,
        addressLine2: firm.addressLine2,
        city: firm.city,
        state: firm.state,
        zipCode: firm.zipCode,
      }
    : EMPTY;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={firmContactSchema}
      onSubmit={save}
    >
      <Form>
        <HubStepShell
          meta={meta}
          index={index}
          onBack={onBack}
          onSkip={onSkip}
          isFirst={isFirst}
          submitting={submitting}
        >
          <FormField name="email" label="Firm email" type="email" placeholder="hello@firm.com" />
          <PhoneField name="phone" label="Phone" />
          <FormField name="addressLine1" label="Address line 1" />
          <FormField name="addressLine2" label="Address line 2" />
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField name="city" label="City" />
            <FormField name="state" label="State" maxLength={2} placeholder="CA" />
            <ZipField name="zipCode" label="ZIP" />
          </div>
        </HubStepShell>
      </Form>
    </Formik>
  );
}
